import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NameAvatar from '../../UI/NameAvatar';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import {
  getAdminContacts,
  getClientContacts,
  getRecruiterContacts,
} from '../../api/messages';
import hireportLogoIcon from '../../icons/logo-ufo.svg';
import { ADMIN_ROLE, CLIENT_ROLE, RECRUITER_ROLE } from '../../constants/roles';
import { Contact } from '../../types';
import { getRandomColor } from '../../constants/palette';
import { MESSAGES } from '../../constants/routes';
import {
  getSupportUnread,
  resetContactCounter,
} from '../../store/messagesSlice';
import Loader from '../../UI/Loader';
import { Badge } from 'antd';

const ContactsList = styled.ul`
  list-style: none;
  width: 17rem;
  max-height: calc(100vh - 160px);
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0.5rem 0;
 @media (max-width: 768px) {
  width: 100%;
  }
`;

const HireportLogo = styled.span`
  width: 53px;
  height: 40px;
  background-image: ${() => "url('" + hireportLogoIcon + "')"};
  background-position: center;
  background-size: 25px 25px;
  background-repeat: no-repeat;
  background-color: #fff;
  border-radius: 50%;
  border: 1px solid #dae5ec;
`;

interface ContactItemProps {
  active?: boolean;
}

const ContactItem = styled.li<ContactItemProps>`
  padding: 0.75rem 1rem;
  position: relative;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  background: ${(props) => (props.active === true ? '#fff' : 'transparent')};
  &:hover {
    background: #fff;
  }
  margin-bottom: 5px;
`;

const ContactItemInfo = styled.div`
  margin-left: 0.5rem;
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
`;

const ContactItemTitle = styled.h5`
  font-family: Lato;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  text-align: left;
  color: #061c2e;
  margin-bottom: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 84%;
`;

const ContactItemDescription = styled.h6`
  font-family: Lato;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  text-align: left;
  color: #627d95;
`;

const UnreadMessagesBadge = styled(Badge)`
  & .ant-badge-count {
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    min-width: 1rem;
    height: 1rem;
    padding: 3px;
    line-height: 1rem;
    background: #34ce72;
    display: flex;
    align-items: center;
    justify-content: center;
    & .ant-scroll-number-only {
      height: auto;
      & > p.ant-scroll-number-only-unit {
        height: auto;
      }
    }
  }
`;

const ContactListHeader = styled.div`
  padding: 1rem 0;
  margin-top: 1rem;
  font-family: Lato;
  font-size: 12px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: 0.96px;
  text-align: left;
  color: #061c2e;
  text-transform: uppercase;
  border-bottom: 1px solid #dae5ec;
  @media (max-width: 768px) {
    display: none;
  }
`;

const ContactListWrapper = styled.div`
    @media (max-width: 768px) {
  &.hide-mobile {
      display: none;
    }
    width: 100vw;
  }
`;

interface ContactListProps extends React.HTMLAttributes<HTMLDivElement> {
  onSelectContact: (contact: Contact) => void;
  searchQuery: string;
  onLoaded: () => void;
}

const ContactList = (props: ContactListProps) => {
  const { searchQuery, onSelectContact, onLoaded, ...rest } = props;
  const [selectedContact, setSelectedContact] = useState<Contact>();
  const [nextPage, setNextPage] = useState(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const loader = useRef(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { contactId } = useParams<{ contactId: string }>();
  const { supportUnread, contactsUnread } = useAppSelector(
    (state) => state.messages,
  );
  const { user } = useAppSelector((state) => state.user);
  const isAdmin = user?.role === ADMIN_ROLE;
  const isClient = user?.role === CLIENT_ROLE;
  const isRecruiter = user?.role === RECRUITER_ROLE;

  const handleSelectContact = useCallback(
    (contact: Contact) => {
      onSelectContact(contact);
      setSelectedContact(contact);
      history.push(MESSAGES + '/' + contact.id);
      if (isAdmin) {
        dispatch(resetContactCounter(contact.id));
      }
    },
    [isAdmin, dispatch, history, onSelectContact],
  );

  const getContacts = useCallback(async () => {
    setLoading(true);
    let result = null;
    let query = '';
    if (searchQuery) {
      query = '?candidate_name=' + searchQuery;
    }
    if (isAdmin) {
      result = await getAdminContacts();
    }
    if (isClient) {
      result = await getClientContacts(query);
    }
    if (isRecruiter) {
      result = await getRecruiterContacts(query);
    }
    if (isClient || isRecruiter) {
      dispatch(getSupportUnread());
    }
    if (result && result.data) {
      setContacts(
        result.data.results.map((c: Contact) => ({
          ...c,
          color: getRandomColor(),
        })),
      );
      if (query) {
        let firstContact = result.data.results[0];
        if (firstContact) {
          handleSelectContact(firstContact);
        }
      }
      setLoading(false);
      onLoaded();
      setNextPage(result.data.pagination.next);
    }
  }, [searchQuery, handleSelectContact, isAdmin, isClient, isRecruiter, onLoaded, dispatch]);

  useEffect(() => {
    getContacts();
  }, [getContacts]);

  useEffect(() => {
    if (loading) {
      // prevent redirect to 404 on page refresh
      return;
    }

    if (contactId) {
      let contact = contacts.find((contact) => contact.id === contactId);
      if (contactId === 'support') {
        contact = {
          id: 'support',
          role: 'support',
          image: '',
          name: 'support',
          unread_message_cnt: supportUnread,
        };
      }

      if (contact) {
        onSelectContact(contact);
        setSelectedContact(contact);
      }
    }
  }, [contactId, contacts, onSelectContact, supportUnread, history, loading]);

  const handleObserver = useCallback(
    async (entities: any) => {
      const cursor = `${history.location.search
        ? history.location.search + '&cursor=' + nextPage
        : '?cursor=' + nextPage
        }`;
      const target = entities[0];
      if (target.isIntersecting && nextPage) {
        // @ts-ignore
        let result = null;
        if (isAdmin) {
          result = await getAdminContacts(cursor);
        }
        if (isClient) {
          result = await getClientContacts(cursor);
        }
        if (isRecruiter) {
          result = await getRecruiterContacts(cursor);
        }
        if (result && result.data) {
          setContacts((state) => [
            ...state,
            // @ts-ignore
            ...result.data.results.map((c: Contact) => ({
              ...c,
              color: getRandomColor(),
            })),
          ]);
          setNextPage(result.data.pagination.next);
        }
      }
    },
    [nextPage, history.location.search, isAdmin, isClient, isRecruiter],
  );

  useEffect(() => {
    const current = loader.current;
    let options = {
      root: null,
      rootMargin: '0px 0px 300px 0px',
      threshold: 1.0,
    };
    const observer = new IntersectionObserver(handleObserver, options);

    if (!nextPage) {
      observer.disconnect();
    } else {
      if (loader && current) {
        observer.observe(current as unknown as Element);
      }
    }
    return () => observer.unobserve(current as unknown as Element);
  }, [nextPage, handleObserver]);

  return (
    <Loader spinning={loading}>
      <ContactListWrapper {...rest}>
        {isClient && <ContactListHeader>{t('RECRUITERS')}</ContactListHeader>}
        {isRecruiter && <ContactListHeader>{t('COMPANIES')}</ContactListHeader>}
        <ContactsList>
          {(isClient || isRecruiter) && (
            <ContactItem
              key="support"
              onClick={() =>
                handleSelectContact({
                  id: 'support',
                  role: 'support',
                  image: '',
                  name: 'support',
                  unread_message_cnt: supportUnread,
                })
              }
              active={
                selectedContact ? selectedContact.id === 'support' || contactId === 'support' : false
              }
            >
              <HireportLogo />
              <ContactItemInfo>
                <ContactItemTitle>
                  {t(
                    isClient
                      ? 'CUSTOMER_SUCCESS_TEAM'
                      : 'HIREPORT_SUPPORT_TITLE',
                  )}
                </ContactItemTitle>
              </ContactItemInfo>
              {supportUnread > 0 && (
                <UnreadMessagesBadge
                  count={supportUnread}
                />
              )}
            </ContactItem>
          )}
          {contacts.map((contact, contactIndex) => {
            let unreadFromStore = (contactsUnread as any)[contact.id];
            let unreadCount = contact.unread_message_cnt;
            if (unreadFromStore !== undefined) {
              unreadCount = unreadFromStore;
            }
            if (
              isAdmin &&
              selectedContact &&
              selectedContact.id === contact.id
            ) {
              unreadCount = 0;
            }
            return (
              <ContactItem
                key={contact.id}
                onClick={() => handleSelectContact(contact)}
                active={
                  selectedContact ? selectedContact.id === contact.id : false
                }
              >
                <NameAvatar
                  name={contact.name}
                  color={contact.color}
                  image={contact.image}
                />
                <ContactItemInfo>
                  <ContactItemTitle>{contact.name}</ContactItemTitle>
                  {isAdmin && (
                    <ContactItemDescription>
                      {contact.role}
                    </ContactItemDescription>
                  )}
                </ContactItemInfo>
                <UnreadMessagesBadge count={unreadCount} />
              </ContactItem>
            );
          })}
        </ContactsList>
        <div className="loader" ref={loader} />
      </ContactListWrapper>
    </Loader>
  );
};

export { ContactList };
